import React from 'react';
import { MapPin, Zap, Target, CheckCircle, Database } from 'lucide-react';

export default function VPRRefinementBadge({ vprRefinement, highPrecisionArea }) {
    if (!vprRefinement && !highPrecisionArea) return null;

    const showVPRBadge = vprRefinement?.refined;
    const showPrecisionBadge = highPrecisionArea?.isHighPrecision;

    if (!showVPRBadge && !showPrecisionBadge) return null;

    return (
        <div className="space-y-3">
            {/* VPR Refinement Badge */}
            {showVPRBadge && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                            <Target className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-gray-900">VPR Enhanced Prediction</h4>
                                <span className="px-2 py-0.5 bg-emerald-600 text-white text-xs font-semibold rounded-full">
                                    REFINED
                                </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">
                                {vprRefinement.message}
                            </p>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="flex items-center gap-2">
                                    <Database className="w-4 h-4 text-emerald-600" />
                                    <span className="text-gray-600">
                                        <span className="font-semibold">{vprRefinement.nearbyImages}</span> verified images
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-emerald-600" />
                                    <span className="text-gray-600">
                                        <span className="font-semibold">±{vprRefinement.precisionMeters}m</span> precision
                                    </span>
                                </div>
                            </div>
                            {vprRefinement.confidenceBoost > 0 && (
                                <div className="mt-2 text-xs text-emerald-700 font-medium">
                                    ✨ Confidence improved by +{Math.round(vprRefinement.confidenceBoost * 100)}%
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* High Precision Area Badge */}
            {showPrecisionBadge && (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-gray-900">High Precision Area</h4>
                                <span className="px-2 py-0.5 bg-yellow-600 text-white text-xs font-semibold rounded-full">
                                    {highPrecisionArea.imageCount}+ IMAGES
                                </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">
                                {highPrecisionArea.message}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                <MapPin className="w-4 h-4 text-yellow-600" />
                                <span>
                                    This area has <span className="font-semibold">{highPrecisionArea.imageCount} verified property images</span> within {highPrecisionArea.radius}m, enabling exceptional location accuracy.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Pro Tip */}
            {!showVPRBadge && !showPrecisionBadge && vprRefinement && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <span className="font-semibold">💡 Pro Tip:</span> {vprRefinement.message}.
                    Help improve accuracy by contributing images!
                </div>
            )}
        </div>
    );
}
